import { useEffect, useState } from "react";
import type { ReactNode } from "react";
import { Check, CircleCheckBig, Plus } from "lucide-react";
import { useRuntime } from "../react/runtime/RuntimeContext";
import { useSession } from "../react/providers/SessionProvider";

// CockpitTasks — the cockpit <tasks> card (form-view revamp Enh 14 · Stage 3A),
// faithful to the frozen mockup's GTD pane: a top capture bar + tasks grouped
// (Next actions · Scheduled · Done) with colour-coded group dots, per-item
// check, title and a meta line (due badge + assignee initials).
//
// Backed by the existing activity.log engine (foundation.communication): planned
// activities attached to this record via related_model / related_id. Marking
// done flips status=done through the standard ede.update pipeline; the capture
// bar creates a planned activity owned by the current user. Mutations reuse the
// platform error toast on veto.

interface TasksCardProps {
    modelKey: string;
    recordRef: string | null;
}

interface TaskRow {
    id: string;
    subject: string;
    status: string;
    dueAt: string | null;
    owner: string;
}

function asText(value: unknown): string {
    if (value == null) return "";
    if (typeof value === "object") {
        const o = value as Record<string, unknown>;
        return typeof o.display_name === "string" ? o.display_name : "";
    }
    return String(value);
}

function initials(name: string): string {
    const parts = name.replace(/\[[^\]]*\]/g, "").trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return "";
    return (parts[0][0] + (parts.length > 1 ? parts[parts.length - 1][0] : "")).toUpperCase();
}

const DAY = 86_400_000;
function endOfToday(): number {
    const d = new Date();
    d.setHours(23, 59, 59, 999);
    return d.getTime();
}
function startOfToday(): number {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d.getTime();
}

// { text, tone } — tone drives the due-badge colour (today / overdue / plain).
function dueMeta(iso: string | null, done: boolean): { text: string; tone: string } | null {
    if (!iso) return null;
    const t = Date.parse(iso);
    if (Number.isNaN(t)) return null;
    const dateStr = new Date(t).toLocaleDateString(undefined, { month: "short", day: "numeric" });
    if (done) return { text: `Completed ${dateStr}`, tone: "" };
    if (t < startOfToday()) {
        const days = Math.max(1, Math.floor((startOfToday() - t) / DAY));
        return { text: `Overdue ${days}d`, tone: "over" };
    }
    if (t <= endOfToday()) return { text: "Due today", tone: "today" };
    if (t <= endOfToday() + DAY) return { text: "Tomorrow", tone: "today" };
    return { text: dateStr, tone: "" };
}

export function CockpitTasks({ modelKey, recordRef }: TasksCardProps): ReactNode {
    const runtime = useRuntime();
    const session = useSession();
    const [tasks, setTasks] = useState<TaskRow[]>([]);
    const [loading, setLoading] = useState(true);
    const [draft, setDraft] = useState("");
    const [defaultTypeId, setDefaultTypeId] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);
    const [epoch, setEpoch] = useState(0);

    useEffect(() => {
        if (!recordRef) {
            setLoading(false);
            return;
        }
        let alive = true;
        void (async () => {
            setLoading(true);
            try {
                const res = await runtime.services.action.searchRecordsByModel(
                    "activity.log",
                    {
                        domain: [
                            ["related_model", "=", modelKey],
                            ["related_id", "=", recordRef],
                        ],
                        order: "due_at asc",
                        limit: 50,
                    },
                    "list",
                );
                if (!alive) return;
                setTasks(
                    (res.records ?? []).map((r) => ({
                        id: String(r.id),
                        subject: asText(r.subject),
                        status: asText(r.status),
                        dueAt: r.due_at ? String(r.due_at) : null,
                        owner: asText(r.owner_user_id),
                    })),
                );
            } catch {
                if (alive) setTasks([]);
            } finally {
                if (alive) setLoading(false);
            }
        })();
        return () => {
            alive = false;
        };
    }, [runtime, modelKey, recordRef, epoch]);

    useEffect(() => {
        let alive = true;
        void (async () => {
            try {
                const res = await runtime.services.action.searchRecordsByModel("activity.type", { limit: 1 }, "list");
                if (alive && res.records?.length) setDefaultTypeId(String(res.records[0].id));
            } catch {
                /* capture bar stays hidden — read-only card still works */
            }
        })();
        return () => {
            alive = false;
        };
    }, [runtime]);

    if (!recordRef) return null;

    const planned = tasks.filter((t) => t.status === "planned");
    const doneTasks = tasks.filter((t) => t.status === "done");
    const eod = endOfToday();
    const isScheduled = (t: TaskRow) => Boolean(t.dueAt) && Date.parse(t.dueAt as string) > eod;
    const groups = [
        { key: "now", label: "Next actions", items: planned.filter((t) => !isScheduled(t)) },
        { key: "sched", label: "Scheduled", items: planned.filter(isScheduled) },
        { key: "done", label: "Done", items: doneTasks },
    ].filter((g) => g.items.length > 0);

    const addTask = async () => {
        const subject = draft.trim();
        if (!subject || !defaultTypeId || busy) return;
        setBusy(true);
        try {
            await runtime.services.record.create("activity.log", {
                related_model: modelKey,
                related_id: recordRef,
                activity_type_id: defaultTypeId,
                owner_user_id: session.userId,
                subject,
                status: "planned",
            });
            setDraft("");
            setEpoch((e) => e + 1);
        } catch {
            /* surfaced by the platform error toast */
        } finally {
            setBusy(false);
        }
    };

    const markDone = async (id: string) => {
        if (busy) return;
        setBusy(true);
        try {
            await runtime.services.record.update("activity.log", id, { status: "done" });
            setEpoch((e) => e + 1);
        } catch {
            /* requires-outcome veto etc. → platform toast */
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="cockpit-card cockpit-card--tasks">
            <div className="cockpit-card__head">
                <CircleCheckBig size={13} />
                Tasks
                {planned.length ? <span className="cockpit-card__badge">{planned.length} open</span> : null}
            </div>
            <div className="cockpit-card__body">
                {defaultTypeId ? (
                    <div className="gtd-capture">
                        <span className="gtd-capture__plus"><Plus size={13} /></span>
                        <input
                            className="gtd-capture__input"
                            placeholder="Capture a task…"
                            value={draft}
                            onChange={(e) => setDraft(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter") void addTask();
                            }}
                            disabled={busy}
                        />
                        <span className="gtd-capture__hint">/due  @assign</span>
                    </div>
                ) : null}

                {loading ? (
                    <p className="cockpit-tasks__empty">Loading…</p>
                ) : groups.length === 0 ? (
                    <p className="cockpit-tasks__empty">No tasks yet.</p>
                ) : (
                    groups.map((g) => (
                        <div className="gtd-group" key={g.key}>
                            <div className="gtd-group__h">
                                <span className={`gtd-group__dot gtd-group__dot--${g.key}`} />
                                {g.label}
                                <span className="gtd-group__count">{g.items.length}</span>
                            </div>
                            {g.items.map((t) => {
                                const done = t.status === "done";
                                const due = dueMeta(t.dueAt, done);
                                const who = initials(t.owner);
                                return (
                                    <div className={`gtd-item${done ? " gtd-item--done" : ""}`} key={t.id}>
                                        <button
                                            type="button"
                                            className={`gtd-check${done ? " gtd-check--done" : ""}`}
                                            onClick={() => !done && markDone(t.id)}
                                            disabled={busy || done}
                                            title={done ? "Done" : "Mark done"}
                                        >
                                            {done ? <Check size={12} /> : null}
                                        </button>
                                        <div className="gtd-item__body">
                                            <div className="gtd-item__title">{t.subject}</div>
                                            {due || who ? (
                                                <div className="gtd-item__meta">
                                                    {due ? (
                                                        <span className={`gtd-due${due.tone ? ` gtd-due--${due.tone}` : ""}`}>{due.text}</span>
                                                    ) : null}
                                                    {due && who ? " · " : null}
                                                    {who ? <span className="gtd-assignee">{who}</span> : null}
                                                </div>
                                            ) : null}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    ))
                )}
            </div>
        </div>
    );
}
